import type { AppMenuItem } from '@camino-solutions/core/app/ui/app-menu';
import { environment } from '@camino-solutions/core/environment';
import type { Injector } from '@angular/core';
import { inject as angularInject, type ProviderToken, runInInjectionContext } from '@angular/core';
import {
  pageFlowGoToByFlowId,
  pageFlowTokenRoutePathGenerator,
} from '@camino-solutions/feature/page-flow/route-helpers';
import { Router } from '@angular/router';
import {
  CoreInviteCompanyRule,
  PlatformProjectsCreateProjectRule,
  SubscriptionsListLicencesRule,
  SubscriptionsListOwnRule,
} from '@camino-solutions/core/access-rule';
import { AppState } from '@camino-solutions/core/app/state';
import { Store } from '@ngxs/store';
import { isNil } from '@camino-solutions/utils/typeguard';
import type { RouteInfos } from '@camino-solutions/core/router';
import { UserService } from '@camino-solutions/core/user/service';

/**
 * Filters menu items recursively to only show items with valid access rules.
 * Parent items without any visible children are removed from the menu.
 *
 * @param items - Menu items to filter
 * @param userService - User service to check access rules
 * @returns Filtered menu items
 */
const filterMenuItems = (
  items: readonly AppMenuItem[],
  userService: UserService,
): AppMenuItem[] => {
  return items
    .map(item => {
      // Deep clone to avoid mutating original
      const clonedItem = { ...item };
      // Track if item originally had children (before filtering)
      const hadChildrenOriginally = !!(item.items && item.items.length > 0);

      // If item has children, filter them recursively first
      if (clonedItem.items && clonedItem.items.length > 0) {
        clonedItem.items = filterMenuItems(clonedItem.items, userService);
      }

      // Store the original state for later filtering
      return { ...clonedItem, __hadChildrenOriginally: hadChildrenOriginally } as AppMenuItem & {
        __hadChildrenOriginally: boolean;
      };
    })
    .filter(item => {
      const itemWithFlag = item as AppMenuItem & { __hadChildrenOriginally?: boolean };

      // Check if item itself has access
      const hasAccess =
        item.accessRule === undefined ? true : userService.hasOperation(item.accessRule.operation);

      if (!hasAccess) {
        return false;
      }

      // If item has no children NOW
      if (!item.items || item.items.length === 0) {
        // Check if it originally had children (parent that became empty after filtering)
        if (itemWithFlag.__hadChildrenOriginally) {
          return false;
        }

        // It's a true leaf node (never had children)
        // However, if this is a wrapper item (no label) with no children, exclude it
        if (!item.label) {
          return false;
        }

        return true;
      }

      // If item has children, only include if at least one child is visible (after filtering)
      return item.items.length > 0;
    })
    .map(item => {
      // Remove the temporary flag before returning
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __hadChildrenOriginally, ...cleanItem } = item as AppMenuItem & {
        __hadChildrenOriginally?: boolean;
      };
      return cleanItem as AppMenuItem;
    });
};

export const menu: (injector: Injector) => readonly AppMenuItem[] = (injector: Injector) => {
  const inject = <T>(token: ProviderToken<T>): T =>
    runInInjectionContext(injector, () => angularInject(token));

  const userService = inject(UserService);

  const menuItems = [
    {
      items: [
        {
          label: 'cmp__layout.LAYOUT.MENU.DASHBOARD',
          icon: 'pi pi-chart-line',
          items: [
            {
              label: 'cmp__layout.LAYOUT.MENU.PROJECT',
              icon: 'pi pi-chart-bar',
              routerLink: ['dashboard/project-progress'],
            },
            {
              label: 'cmp__layout.LAYOUT.MENU.DASHBOARD.CHARTS',
              icon: 'pi pi-chart-bar',
              routerLink: ['dashboard/daily-report'],
            },
          ],
        },
      ],
    },
    {
      items: [
        {
          label: 'cmp__layout.LAYOUT.MENU.COMPANY',
          icon: 'pi pi-building',
          accessRule: CoreInviteCompanyRule.instance,
          items: [
            {
              label: 'cmp__layout.LAYOUT.MENU.COMPANY.LIST',
              icon: 'pi pi-list',
              routerLink: '/companies',
            },
            {
              label: 'cmp__layout.LAYOUT.MENU.COMPANY.NEW',
              icon: 'pi pi-plus',
              command: () => pageFlowGoToByFlowId('company', inject(Router)),
            },
          ],
        },
      ],
    },
    {
      items: [
        {
          label: 'cmp__layout.LAYOUT.MENU.PROJECT',
          icon: 'pi pi-briefcase',
          accessRule: PlatformProjectsCreateProjectRule.instance,
          items: [
            ...inject(Store)
              .selectSnapshot(AppState.menuOperations)
              .map(projectOperation => {
                return {
                  label: projectOperation.menuItem,
                  icon: 'pi pi-plus',
                  command: () => {
                    const flow = projectOperation.flow;
                    if (isNil(flow) === false) {
                      inject(Router).navigate([pageFlowTokenRoutePathGenerator()], {
                        info: {
                          token: { type: 'string', value: flow.token },
                        } satisfies RouteInfos,
                      });
                    }
                    return null;
                  },
                };
              }),
          ],
        },
      ],
    },
    // {
    //   items: [
    //     {
    //       label: 'Dokumentum',
    //       icon: 'pi pi-file',
    //       items: [
    //         {
    //           label: 'Új',
    //           icon: 'pi pi-plus',
    //           command: () => pageFlowGoToByFlowId('document', inject(Router)),
    //         },
    //       ],
    //     },
    //   ],
    // },
    {
      items: [
        {
          label: 'cmp__layout.LAYOUT.MENU.SUBSCRIPTION',
          icon: 'pi pi-wallet',
          items: [
            {
              label: 'cmp__layout.LAYOUT.MENU.SUBSCRIPTION.FIXED',
              icon: 'pi pi-minus',
              accessRule: SubscriptionsListOwnRule.instance,
              routerLink: ['/subscriptions/fixed'],
            },
            {
              label: 'cmp__layout.LAYOUT.MENU.SUBSCRIPTION.FLEXIBLE',
              icon: 'pi pi-wave-pulse',
              accessRule: SubscriptionsListOwnRule.instance,
              routerLink: ['/subscriptions/flexible'],
            },
            {
              label: 'cmp__layout.LAYOUT.MENU.SUBSCRIPTION.LICENCES',
              icon: 'pi pi-shield',
              accessRule: SubscriptionsListLicencesRule.instance,
              routerLink: ['/subscriptions/licences'],
            },
            // {
            //   label: 'cmp__layout.LAYOUT.MENU.SUBSCRIPTION.LICENCEREPORTS',
            //   icon: 'pi pi-chart-bar',
            //   routerLink: ['/subscriptions/licences/reports'],
            // },
          ],
        },
      ],
    },
    {
      items: [
        {
          label: 'cmp__layout.LAYOUT.MENU.TEMPLATES',
          icon: 'pi pi-file',
          items: [
            {
              label: 'cmp__layout.LAYOUT.MENU.TEMPLATES.FORMS',
              icon: 'pi pi-table',
              // accessRule: SubscriptionsListOwnRule.instance,
              routerLink: ['/templates/forms'],
            },
            {
              label: 'cmp__layout.LAYOUT.MENU.TEMPLATES.DOCUMENTS',
              icon: 'pi pi-folder-open',
              // accessRule: SubscriptionsListOwnRule.instance,
              routerLink: ['/templates/documents'],
            },
          ],
        },
      ],
    },
    ...(['development', 'local-docker'].indexOf(environment.name) > -1
      ? [
          // {
          //   label: 'ttttt',
          //   items: [
          //     {
          //       label: 'ttttt',
          //       command: () => {
          //         const router = inject(Router);
          //         const queryParams = {
          //           options: KeyValueStore.create(addMinutes(new Date(), 5)).add(
          //             'EstimationImportContext',
          //             new EstimationImportContext({ tenderId: 'mockTenderId' }),
          //           ),
          //         };
          //         router.navigate(['/import'], { queryParams });
          //       },
          //     },
          //   ],
          // },
          /*           {
            label: 'PIVOT',
            items: [
              {
                label: 'PIVOT',
                icon: 'pi pi-download',
                routerLink: ['/pivot'],
              },
            ],
          },
          {
            label: 'Dani',
            items: [
              {
                label: 'Demo download',
                icon: 'pi pi-download',
                routerLink: ['/dani'],
              },
            ],
          }, */
          /*           {
            label: 'Dani',
            items: [
              {
                label: 'Demo download',
                icon: 'pi pi-download',
                routerLink: ['/dani'],
              },
            ],
          },
                {
            label: 'egg',
            items: [
              {
                label: 'egg',
                icon: 'pi pi-download',
                routerLink: ['/egg/testflowid'],
              },
            ],
          },
           */
        ]
      : []),
    // {
    //   label: 'Test',
    //   // accessRule: TestRule.instance,
    //   items: [
    //     {
    //       label: 'Test form',
    //       routerLink: ['/testform'],
    //       // accessRule: TestRule.instance,
    //     },
    //   ],
    // },
    /*     {
      label: 'Table test panel',
      items: [
        {
          label: 'Debug',
          routerLink: ['/table-test-panel'],
        },
      ],
    },
    {
      label: 'NEW Session panel',
      items: [
        {
          label: 'Panel',
          routerLink: ['/new-session-test-panel'],
        },
      ],
    }, */
  ];

  // Filter menu items to remove parents without visible children
  const filteredMenu = filterMenuItems(menuItems, userService);

  return Object.freeze(filteredMenu);
};
